import { useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import debounce from "lodash/debounce";
import UserContext from "../../../../state/UserContext";
import {
  MultiScoreTableApiEmployee,
  MultiScoreTableApiResponse,
} from "../../../../types/analytics/charts/MultiScoreTableTypes";
import analyticsDynamicWidgetApi from "../../../../api/analytics/analyticsDynamicWidgetApi";
import { AnalyticsWidgetUiDetailsDto } from "../../../../types/analytics/AnalyticsWidgetUiDetailsDto";
import AnalyticsFilterInputs from "../../../../types/analytics/AnalyticsFilterInputs";
import { AnalyticsTableSearchPageDetails } from "../../../../types/analytics/tables/AnalyticsTableSearchPageDetails";
import { AnalyticsWidgetRefreshArguments } from "../../../../types/analytics/AnalyticsWidgetRefreshFilterParams";

const DEFAULT_PAGE_SIZE = 5;

interface ScoreColumn {
  id: string;
  label: string;
  value: number | string | null;
  hexColour: string | null;
}

interface EmployeeData {
  id: string;
  fullName: string;
  jobTitle: string;
  level: string;
  location: string[];
  scores: ScoreColumn[];
}

// Transforms raw API data into a format suitable for rendering
const transformApiData = (
  apiData: [MultiScoreTableApiResponse, MultiScoreTableApiEmployee[]],
): EmployeeData[] => {
  if (!apiData || apiData.length < 2) return [];

  const columns = apiData[0].columns;
  const employees = apiData[1];

  return employees.map((employee) => ({
    id: employee.employeeId.toString(),
    fullName: `${employee.firstName} ${employee.lastName}`,
    jobTitle: employee.jobTitle,
    level: employee.appraisalLevel,
    location: employee.location.split(" > "),
    scores: columns.map((col) => {
      const score = employee.scores.find(
        (s) => s.dataPointId === col.dataPointId,
      );
      return {
        id: col.dataPointId.toString(),
        label: col.columnName,
        value: score ? score.displayValue : null,
        hexColour: score ? score.hexColour : null,
      };
    }),
  }));
};

interface UseMultiScoreTableProps {
  widget: AnalyticsWidgetUiDetailsDto;
  analyticsPageId: number;
  filters: AnalyticsFilterInputs;
}

// Default empty response that matches the expected type
const defaultResponse: [
  MultiScoreTableApiResponse,
  MultiScoreTableApiEmployee[],
] = [
  {
    columns: [],
    totalItemCount: 0,
    currentPageNumber: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  },
  [],
];

export const useMultiScoreTable = ({
  widget,
  analyticsPageId,
  filters,
}: UseMultiScoreTableProps) => {
  const auth = useAuth();
  const dynamicWidgetApi = new analyticsDynamicWidgetApi(
    auth.user?.access_token,
    analyticsPageId,
  );

  // Safely handle null/undefined datasets
  const typeConvertedData = widget?.datasets || defaultResponse;

  const [dataToRender, setDataToRender] = useState<EmployeeData[]>([]);
  const [pageSearchDetails, setPageSearchDetails] =
    useState<AnalyticsTableSearchPageDetails>({
      pageNumber: 1,
      searchPhrase: null,
      refreshDataFromApi: false,
    });
  const [isLoading, setIsLoading] = useState(false);
  const userContext = useContext(UserContext);

  // Initialises table data when widget datasets change
  useEffect(() => {
    initialiseData(widget?.datasets);
  }, [widget?.datasets]);

  // Triggers data refresh when page/search parameters change
  useEffect(() => {
    if (pageSearchDetails.refreshDataFromApi) {
      refreshData();
    }
  }, [pageSearchDetails]);

  /** Debounce the actual state change so it doesn't call the API to refresh the data on each keystroke */
  const debouncedUpdateSearchState = useCallback(
    debounce((searchValue: string | null) => {
      setPageSearchDetails({
        pageNumber: 1,
        searchPhrase: searchValue,
        refreshDataFromApi: true,
      });
    }, 300),
    [],
  );

  const initialiseData = (datasets: any[] | undefined) => {
    if (!datasets || datasets.length < 2) {
      setDataToRender([]);
      return;
    }

    const convertedData = transformApiData(
      datasets as [MultiScoreTableApiResponse, MultiScoreTableApiEmployee[]],
    );
    setDataToRender(convertedData);
  };

  const handlePageChange = (pageNumber: number) => {
    setPageSearchDetails({
      ...pageSearchDetails,
      pageNumber,
      refreshDataFromApi: true,
    });
  };

  const handleSearchChange = (newSearchPhrase: string | null) => {
    debouncedUpdateSearchState(newSearchPhrase);
  };

  const refreshData = () => {
    setIsLoading(true);

    const successCallback = (data: AnalyticsWidgetUiDetailsDto) => {
      initialiseData(data?.datasets ? data.datasets : defaultResponse);
      setIsLoading(false);
    };

    const errorCallback = (error: any) => {
      setIsLoading(false);
      console.error(error);
      // On error, ensure we have empty data rather than stale data
      setDataToRender([]);
    };

    const refreshParams: AnalyticsWidgetRefreshArguments = {
      analyticsPageId: analyticsPageId,
      widgetId: widget.dynamicWidgetId!,
      pageNumber: pageSearchDetails.pageNumber,
      pageSize: typeConvertedData[0]?.pageSize || DEFAULT_PAGE_SIZE,
      searchValue: pageSearchDetails.searchPhrase,
    };

    dynamicWidgetApi.refreshWidgetData(
      filters,
      refreshParams,
      successCallback,
      errorCallback,
    );
  };

  const noResultsLoaded = !isLoading && dataToRender.length === 0;
  const pageSize = typeConvertedData[0]?.pageSize || DEFAULT_PAGE_SIZE;
  const totalItemCount = typeConvertedData[0]?.totalItemCount || 0;

  return {
    dataToRender,
    isLoading,
    noResultsLoaded,
    pageSize,
    totalItemCount,
    pageSearchDetails,
    handlePageChange,
    handleSearchChange,
    userContext,
  };
};
